import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AuthGuard, RoleGuard } from './components/RoleGuard';
import { SidebarLayout } from './components/SidebarLayout';
import { CopilotProvider } from './copilot';
import { LoginPage } from './pages/LoginPage';
import { HubDashboard } from './pages/HubDashboard';
import { ActivityPage } from './pages/ActivityPage';
import { AdoptionAnalyticsPage } from './pages/AdoptionAnalyticsPage';
import { AskStewieAnalyticsPage } from './pages/AskStewieAnalyticsPage';
import { AskStewiePage } from './pages/AskStewiePage';
import { QuickCanvassPage } from './pages/QuickCanvassPage';
import { QuickCanvassBidBoard } from './pages/QuickCanvassBidBoard';
import { DraftBoardPage } from './pages/DraftBoardPage';
import { CommandCenterPage } from './pages/CommandCenterPage';
import { StationLogBookPage } from './pages/StationLogBookPage';
import { RFIDTrackerPage } from './pages/RFIDTrackerPage';
import { AdminPage } from './pages/AdminPage';
import { AdminOvertimePage } from './pages/AdminOvertimePage';
import { TimesheetsPage } from './pages/TimesheetsPage';
import { TimesheetEditorPage } from './pages/TimesheetEditorPage';
import { DemoAppPage } from './pages/DemoAppPage';
import { QuickCanvassModule } from './modules/quickcanvass';
import { QCSSOStartPage } from './modules/quickcanvass/QCSSOStartPage';
import { AuthCallbackPage } from './pages/quick-canvass/AuthCallbackPage';
import { AppsHubPage } from './pages/AppsHubPage';
import './App.css';

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">Loading GETGRIDGATE...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />

      {/* Protected routes with Copilot */}
      <Route
        path="/dashboard"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <HubDashboard />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/activity"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <ActivityPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/analytics/adoption/:department"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <AdoptionAnalyticsPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/analytics/adoption/:department/askstewie"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <AskStewieAnalyticsPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/askstewie"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <AskStewiePage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/canvass"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <DraftBoardPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/canvass/bid-board"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <QuickCanvassBidBoard />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/canvass/manage"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <QuickCanvassPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      {/* Quick Canvass Legacy Module */}
      <Route
        path="/quickcanvass/*"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <QuickCanvassModule />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/command-center"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['CREW_LEADER', 'SUPERVISOR', 'ADMIN', 'AUDITOR']}>
              <CopilotProvider>
                <SidebarLayout>
                  <CommandCenterPage />
                </SidebarLayout>
              </CopilotProvider>
            </RoleGuard>
          </AuthGuard>
        }
      />

      <Route
        path="/station-log"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <StationLogBookPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/rfid"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <RFIDTrackerPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/admin"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['ADMIN']}>
              <CopilotProvider>
                <SidebarLayout>
                  <AdminPage />
                </SidebarLayout>
              </CopilotProvider>
            </RoleGuard>
          </AuthGuard>
        }
      />

      <Route
        path="/admin/overtime"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['ADMIN']}>
              <CopilotProvider>
                <SidebarLayout>
                  <AdminOvertimePage />
                </SidebarLayout>
              </CopilotProvider>
            </RoleGuard>
          </AuthGuard>
        }
      />

      {/* Timesheets Module (Internal) */}
      <Route
        path="/timesheets"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <TimesheetsPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      <Route
        path="/timesheets/:id"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <TimesheetEditorPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      {/* Demo App - OIDC SSO Validation (Public - has its own auth) */}
      <Route path="/demo-app" element={<DemoAppPage />} />

      {/* Quick Canvass SSO Start (Public - initiates SSO flow) */}
      <Route path="/quick-canvass/sso/start" element={<QCSSOStartPage />} />

      {/* Quick Canvass Auth Callback (Public - handles OIDC callback) */}
      <Route path="/quick-canvass/auth/callback" element={<AuthCallbackPage />} />

      {/* Apps Hub - SSO Launch Pad */}
      <Route
        path="/apps"
        element={
          <AuthGuard>
            <CopilotProvider>
              <SidebarLayout>
                <AppsHubPage />
              </SidebarLayout>
            </CopilotProvider>
          </AuthGuard>
        }
      />

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #e2e8f0',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
